import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostListener, ViewChild, inject } from '@angular/core';
import { DashboardSearchBase } from '../common/dashboard-search-base';
import { DashboardBulkSelectionService } from '../../../services/dashboard-bulk-selection.service';
import { isActiveLogisticsFilterTag, logisticsMagicFieldDisplayName } from './logistics-dashboard.config';
import { LOGISTICS_FILTER_HELP } from './logistics-filter-help';

/**
 * Entitled logistics prospecting workspace.
 *
 * It owns the logistics-specific information architecture and results
 * presentation while inheriting the same filter state and request
 * serialisation used by the full dashboard.
 */
@Component({
  selector: 'app-logistics-dashboard',
  templateUrl: './logistics-dashboard.component.html',
  styleUrls: ['./logistics-dashboard.component.css'],
  standalone: false,
  providers: [DashboardBulkSelectionService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LogisticsDashboardComponent extends DashboardSearchBase {
  readonly logisticsFilterHelp = LOGISTICS_FILTER_HELP;
  readonly logisticsMagicFieldDisplayName = logisticsMagicFieldDisplayName;
  private readonly logisticsCdr = inject(ChangeDetectorRef);
  @ViewChild('logisticsProductInput') logisticsProductInput?: ElementRef<HTMLInputElement>;
  @ViewChild('logisticsFilterPane') logisticsFilterPane?: ElementRef<HTMLElement>;
  @ViewChild('logisticsRefineButton') logisticsRefineButton?: ElementRef<HTMLButtonElement>;
  @ViewChild('logisticsRestoreFiltersButton') logisticsRestoreFiltersButton?: ElementRef<HTMLButtonElement>;
  @ViewChild('logisticsCollapseFiltersButton') logisticsCollapseFiltersButton?: ElementRef<HTMLButtonElement>;

  logisticsProductDraft = '';
  logisticsSearchElapsedSeconds = 0;
  logisticsHasSearched = false;
  logisticsFiltersExpanded = false;
  logisticsFiltersCollapsed = false;
  logisticsFilterDrawerOpen = false;
  private logisticsAppliedFilterSignature = '';
  private logisticsCriteriaRefreshTimer?: ReturnType<typeof setTimeout>;
  private logisticsSearchTimer?: ReturnType<typeof setInterval>;

  get isLogisticsSearchLoading(): boolean {
    return this.isLoading().loadingCompanies || this.isLoading().loadingCompanyCount;
  }

  get logisticsHasPendingChanges(): boolean {
    return this.logisticsHasSearched && (
      this.logisticsProductDraft.trim().length > 0 ||
      this.logisticsFilterSignature() !== this.logisticsAppliedFilterSignature
    );
  }

  private logisticsFilterSignature(): string {
    return JSON.stringify(this.searchTags
      .filter(isActiveLogisticsFilterTag)
      .map(({ param, value }) => ({ param, value: String(value) }))
      .sort((a, b) => a.param.localeCompare(b.param) || a.value.localeCompare(b.value)));
  }

  runLogisticsSearch(): void {
    if (this.isLogisticsSearchLoading) return;
    this.logisticsHasSearched = true;
    this.logisticsFiltersExpanded = false;
    this.logisticsFiltersCollapsed = false;
    this.logisticsFilterDrawerOpen = false;
    this.logisticsSearchElapsedSeconds = 0;
    this.searchCompaniesAfterFilters(true);
    this.logisticsAppliedFilterSignature = this.logisticsFilterSignature();
    clearInterval(this.logisticsSearchTimer);
    this.logisticsSearchTimer = setInterval(() => {
      if (!this.isLogisticsSearchLoading) {
        clearInterval(this.logisticsSearchTimer);
        return;
      }
      this.logisticsSearchElapsedSeconds += 1;
      this.logisticsCdr.detectChanges();
    }, 1000);
    setTimeout(() => this.scrollToLogisticsResults());
  }

  editLogisticsFilters(): void {
    if (window.matchMedia('(min-width: 1440px)').matches) {
      this.logisticsFiltersCollapsed = false;
      this.logisticsFiltersExpanded = true;
      setTimeout(() => this.logisticsFilterPane?.nativeElement.scrollIntoView({ behavior: 'instant', block: 'start' }));
    } else {
      this.logisticsFilterDrawerOpen = true;
      setTimeout(() => this.logisticsFilterPane?.nativeElement.focus());
    }
    this.logisticsCdr.markForCheck();
  }

  closeLogisticsFilterDrawer(): void {
    this.logisticsFilterDrawerOpen = false;
    this.logisticsCdr.markForCheck();
    setTimeout(() => this.logisticsRefineButton?.nativeElement.focus());
  }

  showLogisticsResults(): void {
    this.logisticsFiltersExpanded = false;
    this.logisticsCdr.markForCheck();
    setTimeout(() => this.scrollToLogisticsResults());
  }

  collapseLogisticsFilters(): void {
    this.logisticsFiltersCollapsed = true;
    this.logisticsCdr.markForCheck();
    setTimeout(() => this.logisticsRestoreFiltersButton?.nativeElement.focus());
  }

  restoreLogisticsFilters(): void {
    this.logisticsFiltersCollapsed = false;
    this.logisticsCdr.markForCheck();
    setTimeout(() => this.logisticsCollapseFiltersButton?.nativeElement.focus());
  }

  override scrollToLogisticsResults(): void {
    if (this.logisticsHasSearched && !this.logisticsFiltersExpanded && window.matchMedia('(min-width: 1440px)').matches) {
      window.scrollTo({ top: 0, behavior: 'instant' });
    } else {
      super.scrollToLogisticsResults();
    }
  }

  @HostListener('document:keydown.escape')
  onLogisticsEscape(): void {
    if (this.logisticsFilterDrawerOpen) this.closeLogisticsFilterDrawer();
  }

  @HostListener('window:resize')
  onLogisticsResize(): void {
    if (window.matchMedia('(min-width: 1440px)').matches) {
      this.logisticsFilterDrawerOpen = false;
    } else if (this.logisticsHasSearched) {
      this.logisticsFiltersExpanded = false;
    }
    this.logisticsCdr.markForCheck();
  }

  queueLogisticsCriteriaRefresh(): void {
    clearTimeout(this.logisticsCriteriaRefreshTimer);
    this.logisticsCriteriaRefreshTimer = setTimeout(() => {
      this.refreshSearchTagsFromCurrentFilters();
      this.logisticsCdr.markForCheck();
    }, 120);
  }

  commitLogisticsProductTerm(event: KeyboardEvent): void {
    event.preventDefault();
    this.setLogisticsProductTerm(this.logisticsProductDraft);
  }

  setLogisticsProductTerm(term: string): void {
    const trimmedTerm = term.trim();
    if (!trimmedTerm) return;
    this.summarySemanticSearchTerm = trimmedTerm;
    this.logisticsProductDraft = '';
    this.refreshSearchTagsFromCurrentFilters();
  }

  clearLogisticsProductTerm(): void {
    this.summarySemanticSearchTerm = '';
    this.logisticsProductDraft = '';
    this.enforceSemanticScoreSortDefault();
    this.refreshSearchTagsFromCurrentFilters();
    setTimeout(() => this.logisticsProductInput?.nativeElement.focus());
  }

  override searchCompaniesAfterFilters(initialisePageCounter: boolean): void {
    this.setLogisticsProductTerm(this.logisticsProductDraft);
    this.logisticsHasSearched = true;
    super.searchCompaniesAfterFilters(initialisePageCounter);
    this.logisticsAppliedFilterSignature = this.logisticsFilterSignature();
  }

  override resetLogisticsFilters(): void {
    this.logisticsProductDraft = '';
    super.resetLogisticsFilters();
  }

  override ngOnDestroy(): void {
    clearTimeout(this.logisticsCriteriaRefreshTimer);
    clearInterval(this.logisticsSearchTimer);
    super.ngOnDestroy();
  }
}
